// Én tjeneste under Tjenester og priser (/tjenester/:id, /tjenester/ny): navn, pris, tid,
// booking, grupper, sjekkliste og resten av feltene som før lå i Hygraph. Alle kan se; bare
// leder og mekaniker kan lagre (backenden sjekker det samme).
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Tilbake, Sidetopp, Tekstfelt, Valgfelt, knappPrimar, knappSekundar, feltKlasse } from '../../ny-kundesamhandling/Skjema';
import Ikon from '../../ny-kundesamhandling/Ikon';
import { useKanEndrePriser } from '../../employeeAccess';
import {
  KATEGORI_NAVN, BOOKING_NAVN, SERVICETYPE_NAVN, SYKKELTYPE_NAVN, lagreIKatalog, kr, beggeSiderPris, useKatalogAdmin, sistEndret, endredeFelt,
  lastOppBilde, fjernBilde,
} from './katalogFelles';
import { Avkrysning, Flervalg, Tallfelt, Melding, Endringslogg } from './Velgere';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TILBAKE = '/tjenester';

// Sluggene koden kjenner igjen; endres de, slutter noe å virke (dekkhotell, dekkskift og
// kombipriser i royken-sykkelservice public/js/tjenestepris.js, sykkelhotell, etterjustering i
// statistikken, elsykkel og programvare i bookingen).
const VIKTIGE_SLUGGER = [
  'dekk-slangeskift', 'dekk-slangeskift-2-hjul', 'dekkhotell', 'sykkelhotell', 'etterjustering',
  'softwareoppdatering', 'sykkelservice-el-sykkel', 'lufting-bremser', 'lufting-bremser-begge', 'girjustering',
  'bremsejustering',
];

// Tjenester der prisen for begge sider er en egen tjeneste (BEGGE_SIDER_SLUG i tjenestepris.js).
const BEGGE_SIDER_TJENESTE = { 'dekk-slangeskift': 'dekk-slangeskift-2-hjul', 'lufting-bremser': 'lufting-bremser-begge' };

// Har tjenesten kommentarfelt når Kommentarfelt ikke er satt? Samme regel som harKommentarfelt i
// royken-sykkelservice src/pages/sykkelservice/index.astro.
const kommentarSomStandard = (t) => {
  const navn = `${t.slug} ${t.tjeneste}`.toLowerCase().replace(/[^a-z0-9 ]/g, '');
  return (t.servicetype || []).some((s) => s.toLowerCase() === 'servicepakker') || !!t.foranbak
    || navn.includes('annet') || /dekkskift|slangeskift|dekkslange/.test(navn.replace(/ /g, ''));
};

const FELT = [
  'tjeneste', 'slug', 'kategori', 'aktiv', 'kunIButikk', 'tjenestebeskrivelse', 'pris', 'tidsbruk', 'gratisarbeid', 'foranbak',
  'dobbelPris', 'dobbelPrisRabatt', 'kommentarfelt',
  'antall', 'booking', 'leveringstidDager', 'samlesUkentlig', 'tillattButikkIds', 'grupper', 'servicetype',
  'sykkeltyper', 'sjekkpunkter', 'sortering', 'verkstedmal', 'seoTittel', 'seoBeskrivelse',
];

const tomTjeneste = {
  tjeneste: '', slug: '', kategori: 'sykkelservice', aktiv: true, kunIButikk: false, tjenestebeskrivelse: '', pris: null, tidsbruk: 0,
  gratisarbeid: false, foranbak: false, dobbelPris: false, dobbelPrisRabatt: null, kommentarfelt: null, antall: null, booking: ['dag', 'uke'], leveringstidDager: null,
  samlesUkentlig: false, tillattButikkIds: [], grupper: [], servicetype: [], sykkeltyper: [], sjekkpunkter: [],
  sortering: null, verkstedmal: '', seoTittel: '', seoBeskrivelse: '',
};

function Seksjon({ id, tittel, children }) {
  return (
    <section aria-labelledby={id} className="space-y-3 rounded-2xl border-2 border-slate-200 bg-white p-3 sm:p-3">
      <h2 id={id} className="text-base font-bold text-slate-900">{tittel}</h2>
      {children}
    </section>
  );
}

// Bildene på tjenesten. Lagres med en gang (ikke med «Lagre»), siden filen må lastes opp.
const BILDER_MAKS = 10;
const BILDE_MAKS_MB = 10;

function Bilder({ tjeneste, kanEndre, onEndret }) {
  const [status, setStatus] = useState({ type: '', tekst: '' });
  const bilder = tjeneste.tjenestebilde || [];
  const opptatt = status.type === 'lagrer';

  async function velgFil(e) {
    const fil = e.target.files?.[0];
    e.target.value = '';
    if (!fil) return;
    if (fil.size > BILDE_MAKS_MB * 1024 * 1024) { setStatus({ type: 'feil', tekst: `Bildet er større enn ${BILDE_MAKS_MB} MB.` }); return; }
    setStatus({ type: 'lagrer', tekst: 'Laster opp bildet …' });
    try {
      await lastOppBilde(tjeneste._id, fil);
      await onEndret();
      setStatus({ type: 'ok', tekst: 'Bildet er lagt til.' });
    } catch (err) {
      setStatus({ type: 'feil', tekst: err.message });
    }
  }

  async function fjern(b, nr) {
    if (!window.confirm(`Fjerne bilde ${nr} fra «${tjeneste.tjeneste}»?`)) return;
    setStatus({ type: 'lagrer', tekst: 'Fjerner bildet …' });
    try {
      await fjernBilde(tjeneste._id, b.url);
      await onEndret();
      setStatus({ type: 'ok', tekst: `Bilde ${nr} er fjernet.` });
    } catch (err) {
      setStatus({ type: 'feil', tekst: err.message });
    }
  }

  return (
    <Seksjon id="tr-bilder" tittel="Bilder">
      {bilder.length === 0 ? (
        <p className="text-slate-700">Ingen bilder.</p>
      ) : (
        <ul aria-label="Bilder" className="flex flex-wrap gap-3">
          {bilder.map((b, i) => (
            <li key={b.url} className="space-y-2">
              <img src={b.url} alt={`Bilde ${i + 1} av ${tjeneste.tjeneste}`} className="h-28 w-auto max-w-[12rem] rounded-lg border border-slate-200 object-contain" />
              {kanEndre && (
                <button type="button" onClick={() => fjern(b, i + 1)} disabled={opptatt} className={`${knappSekundar} min-h-[2.25rem] py-1.5`}>
                  Fjern<span className="sr-only"> bilde {i + 1}</span>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {kanEndre && bilder.length < BILDER_MAKS && (
        <div>
          <label htmlFor="tr-bilde-fil" className="mb-1 block font-semibold text-slate-800">Legg til bilde</label>
          <input id="tr-bilde-fil" type="file" accept="image/*" onChange={velgFil} disabled={opptatt} aria-describedby="tr-bilde-hjelp"
            className="block w-full max-w-md text-slate-800 file:mr-3 file:min-h-[2.25rem] file:cursor-pointer file:rounded-xl file:border-2 file:border-slate-300 file:bg-white file:px-3 file:font-semibold file:text-slate-900 hover:file:bg-slate-50" />
          <p id="tr-bilde-hjelp" className="mt-1.5 text-sm text-slate-600">JPG, PNG eller lignende, maks {BILDE_MAKS_MB} MB. Lagres med en gang.</p>
        </div>
      )}
      {opptatt && <p role="status" className="text-slate-700">{status.tekst}</p>}
      {status.type === 'ok' && <Melding type="ok">{status.tekst}</Melding>}
      {status.type === 'feil' && <Melding type="feil">{status.tekst}</Melding>}
    </Seksjon>
  );
}

// Sjekklisten: punktene er felles for flere tjenester. Her velges hvilke som hører til denne
// tjenesten, og nye punkter kan legges til.
function Sjekkliste({ alle, tjenester, valgte, onEndre, kanEndre, onNyttPunkt }) {
  const [nytt, setNytt] = useState('');
  const [leggTil, setLeggTil] = useState('');
  const [feil, setFeil] = useState('');
  const perId = useMemo(() => new Map(alle.map((p) => [p._id, p])), [alle]);
  const brukAv = (id) => tjenester.filter((t) => (t.sjekkpunkter || []).includes(id)).length;
  const valgtePunkter = valgte.map((id) => perId.get(id)).filter(Boolean)
    .sort((a, b) => (a.sortering ?? Infinity) - (b.sortering ?? Infinity) || (a._id < b._id ? -1 : 1));
  const ledige = alle.filter((p) => !valgte.includes(p._id));

  async function opprett() {
    if (!nytt.trim()) return;
    setFeil('');
    try {
      const sortering = Math.max(0, ...valgtePunkter.map((p) => p.sortering || 0)) + 1;
      const punkt = await lagreIKatalog('POST', 'sjekkpunkter', { sjekkpunkt: nytt.trim(), sortering, ok: true });
      onNyttPunkt(punkt);
      onEndre([...valgte, punkt._id]);
      setNytt('');
    } catch (err) {
      setFeil(err.message);
    }
  }

  return (
    <div className="space-y-2">
      {valgtePunkter.length ? (
        <ol className="space-y-2">
          {valgtePunkter.map((p) => (
            <li key={p._id} className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-1.5">
              <span className="w-8 shrink-0 text-right text-sm text-slate-600">{p.sortering ?? '–'}</span>
              <span className="min-w-0 flex-1 font-medium text-slate-900 [overflow-wrap:anywhere]">{p.sjekkpunkt || p.slug}</span>
              <span className="text-sm text-slate-600">{brukAv(p._id) > 1 ? `brukes av ${brukAv(p._id)} tjenester` : ''}</span>
              {kanEndre && (
                <button type="button" onClick={() => onEndre(valgte.filter((id) => id !== p._id))}
                  className="min-h-[2.25rem] rounded-lg px-3 font-semibold text-red-800 hover:bg-red-50">
                  Fjern<span className="sr-only"> {p.sjekkpunkt}</span>
                </button>
              )}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-slate-700">Ingen sjekkpunkter.</p>
      )}
      {kanEndre && (
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="flex items-end gap-2">
            <Valgfelt id="sj-legg-til" etikett="Legg til et punkt som finnes" value={leggTil} onChange={(e) => setLeggTil(e.target.value)} className="flex-1">
              <option value="">Velg punkt</option>
              {ledige.map((p) => <option key={p._id} value={p._id}>{p.sjekkpunkt || p.slug}</option>)}
            </Valgfelt>
            <button type="button" disabled={!leggTil} onClick={() => { onEndre([...valgte, leggTil]); setLeggTil(''); }} className={knappSekundar}>Legg til</button>
          </div>
          <div className="flex items-end gap-2">
            <div className="min-w-0 flex-1">
              <label htmlFor="sj-nytt" className="mb-1 block font-semibold text-slate-800">Nytt punkt</label>
              <input id="sj-nytt" type="text" value={nytt} onChange={(e) => setNytt(e.target.value)} maxLength={200} className={feltKlasse} />
            </div>
            <button type="button" disabled={!nytt.trim()} onClick={opprett} className={knappSekundar}>Opprett</button>
          </div>
        </div>
      )}
      {feil && <Melding type="feil">{feil}</Melding>}
      <p className="text-sm text-slate-600">Rekkefølgen følger tallet foran punktet. Punktene er felles, så teksten på et punkt endres for alle tjenestene som bruker det.</p>
    </div>
  );
}

// Hva kunden betaler når både foran og bak er valgt.
function BeggeSider({ skjema, sett, alle }) {
  const beggeSlug = BEGGE_SIDER_TJENESTE[skjema.slug];
  if (beggeSlug) {
    const begge = alle.find((t) => t.slug === beggeSlug);
    return (
      <p className="text-sm text-slate-700">
        Foran og bak: prisen på {begge ? <Link to={`/tjenester/${begge._id}`} className="font-semibold text-indigo-800 underline">{begge.tjeneste}</Link> : beggeSlug} ({kr(begge?.pris)}).
      </p>
    );
  }
  const utenPris = typeof skjema.pris !== 'number';
  return (
    <div className="space-y-3">
      <Avkrysning id="tr-dobbelpris" etikett="Doble prisen" hjelp="Foran og bak koster prisen × 2. Av: samme pris uansett sider."
        verdi={skjema.dobbelPris} onEndre={sett('dobbelPris')} />
      {skjema.dobbelPris && (
        <div className="flex flex-wrap items-end gap-3">
          <Tallfelt id="tr-dobbelrabatt" etikett="Rabatt når prisen dobles" enhet="%" hjelp="Tomt: ingen rabatt. Høyst 50 %."
            verdi={skjema.dobbelPrisRabatt} onEndre={sett('dobbelPrisRabatt')} />
          <p role="status" className="pb-2 font-semibold text-slate-800">
            {utenPris ? 'Sett pris for å se summen.' : `Begge sider: ${kr(beggeSiderPris(skjema.pris, skjema.dobbelPrisRabatt))}`}
          </p>
        </div>
      )}
    </div>
  );
}

export default function TjenesteRediger() {
  const { id } = useParams();
  const ny = id === 'ny';
  const navigate = useNavigate();
  const kanEndre = useKanEndrePriser();
  const { data, feil: hentFeil, hent } = useKatalogAdmin();
  const [skjema, setSkjema] = useState(null);
  const [skjemaFor, setSkjemaFor] = useState(null); // id-en skjemaet er fylt ut for
  const [original, setOriginal] = useState(null); // skjemaet slik det var da det ble fylt ut eller lagret
  const [butikker, setButikker] = useState([]);
  const [ekstraPunkter, setEkstraPunkter] = useState([]);
  const [status, setStatus] = useState({ type: '', tekst: '' });

  const tjeneste = useMemo(() => (ny ? null : data?.tjenester.find((t) => t._id === id)), [data, id, ny]);

  // Fylles ut én gang per id, så lagring (som henter katalogen på nytt) ikke overskriver skjemaet.
  useEffect(() => {
    if (!data || skjemaFor === id) return;
    let fylt;
    if (ny) fylt = { ...tomTjeneste };
    else if (tjeneste) fylt = Object.fromEntries(FELT.map((f) => [f, tjeneste[f] ?? tomTjeneste[f]]));
    else return;
    setSkjema(fylt);
    setOriginal(fylt);
    setSkjemaFor(id);
  }, [data, id, ny, tjeneste, skjemaFor]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/stores`).then((r) => (r.ok ? r.json() : [])).then(setButikker).catch(() => setButikker([]));
  }, []);

  const sett = (felt) => (v) => setSkjema((s) => ({ ...s, [felt]: v }));

  async function lagre(e) {
    e.preventDefault();
    if (!skjema.tjeneste.trim()) { setStatus({ type: 'feil', tekst: 'Skriv navnet på tjenesten.' }); return; }
    setStatus({ type: 'lagrer', tekst: '' });
    try {
      const data = ny ? { ...skjema } : endredeFelt(skjema, original);
      if (ny && !data.slug) delete data.slug; // lages av navnet på serveren
      if (!ny && !Object.keys(data).length) { setStatus({ type: 'ok', tekst: 'Ingen endringer å lagre.' }); return; }
      const lagret = await lagreIKatalog(ny ? 'POST' : 'PATCH', ny ? 'tjenester' : `tjenester/${id}`, data);
      await hent();
      setOriginal(skjema);
      if (ny) navigate(`/tjenester/${lagret._id}`, { replace: true });
      setStatus({ type: 'ok', tekst: 'Lagret. Endringen vises på nettsiden innen et par minutter.' });
    } catch (err) {
      setStatus({ type: 'feil', tekst: err.message });
    }
  }

  if (hentFeil) return <div className="mx-auto max-w-7xl space-y-3"><Tilbake til={TILBAKE}>Tilbake til tjenestene</Tilbake><Melding type="feil">{hentFeil}</Melding></div>;
  if (data && !ny && !tjeneste) return <div className="mx-auto max-w-7xl space-y-3"><Tilbake til={TILBAKE}>Tilbake til tjenestene</Tilbake><Melding type="feil">Fant ikke tjenesten.</Melding></div>;
  if (!skjema || skjemaFor !== id) return <div className="mx-auto max-w-7xl space-y-3"><Tilbake til={TILBAKE}>Tilbake til tjenestene</Tilbake><p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter tjenesten …</p></div>;

  const grupper = data.grupper.filter((g) => g.kategori === skjema.kategori || skjema.grupper.includes(g._id));
  const sykkel = skjema.kategori === 'sykkelservice';
  const trykk = skjema.kategori === 'tekstiltrykk';
  const viktigSlug = !ny && VIKTIGE_SLUGGER.includes(tjeneste?.slug);
  const alleSjekkpunkter = [...data.sjekkpunkter, ...ekstraPunkter.filter((p) => !data.sjekkpunkter.some((d) => d._id === p._id))];

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Tilbake til={TILBAKE}>Tilbake til tjenestene</Tilbake>
      <Sidetopp ikon="skiftenokkel" farge="verksted" tittel={ny ? 'Ny tjeneste' : skjema.tjeneste || 'Tjeneste'}
        undertittel={ny ? 'Legges til i bookingen på nettsiden når den er aktiv og står i en gruppe.' : sistEndret(tjeneste)} />

      {!kanEndre && <Melding>Bare leder og mekaniker kan endre tjenester og priser; velg deg selv øverst i menyen.</Melding>}

      <form onSubmit={lagre} className="space-y-4">
        <fieldset disabled={!kanEndre} className="space-y-4">
          <legend className="sr-only">Tjenesten</legend>

          <Seksjon id="tr-grunn" tittel="Tjenesten">
            <div className="grid gap-3 sm:grid-cols-2">
              <Tekstfelt id="tr-navn" etikett="Navn" value={skjema.tjeneste} onChange={(e) => sett('tjeneste')(e.target.value)} required maxLength={120} />
              <Valgfelt id="tr-kategori" etikett="Kategori" value={skjema.kategori} onChange={(e) => sett('kategori')(e.target.value)}>
                {Object.entries(KATEGORI_NAVN).map(([v, n]) => <option key={v} value={v}>{n}</option>)}
              </Valgfelt>
            </div>
            <Tekstfelt id="tr-beskrivelse" etikett="Beskrivelse på nettsiden" flerlinjet rows={5} value={skjema.tjenestebeskrivelse}
              onChange={(e) => sett('tjenestebeskrivelse')(e.target.value)} hjelp="Én linje per punkt blir en punktliste på nettsiden og plakaten." />
            <Avkrysning id="tr-aktiv" etikett="Aktiv" hjelp="Av: vises ikke på nettsiden eller når service registreres."
              verdi={skjema.aktiv !== false} onEndre={sett('aktiv')} />
            <Avkrysning id="tr-butikk" etikett="Bare i butikken"
              hjelp={trykk ? 'Trykk selges bare i butikken.' : 'Vises ikke på nettsiden og kan ikke bookes der, men kan velges når service registreres.'}
              verdi={trykk || !!skjema.kunIButikk} onEndre={sett('kunIButikk')} disabled={trykk} />
          </Seksjon>

          <Seksjon id="tr-pris" tittel="Pris og tid">
            <div className="flex flex-wrap gap-3">
              <Tallfelt id="tr-prisfelt" etikett="Pris" enhet="kr" verdi={skjema.pris} onEndre={sett('pris')} />
              <Tallfelt id="tr-tid" etikett="Tidsbruk" enhet="min" hjelp="Brukes i kapasiteten" verdi={skjema.tidsbruk} onEndre={sett('tidsbruk')} />
            </div>
            <div className="flex flex-wrap gap-3">
              <Avkrysning id="tr-gratis" etikett="Gratisarbeid" hjelp="Telles som tidsstyrt arbeid uten pris i statistikken." verdi={skjema.gratisarbeid} onEndre={sett('gratisarbeid')} />
              {sykkel && <Avkrysning id="tr-foranbak" etikett="Foran/bak" hjelp="Kunden velger foran, bak eller begge." verdi={skjema.foranbak} onEndre={sett('foranbak')} />}
            </div>
            {sykkel && skjema.foranbak && <BeggeSider skjema={skjema} sett={sett} alle={data.tjenester} />}
          </Seksjon>

          <Seksjon id="tr-booking" tittel="Booking">
            {sykkel ? (
              <>
                <Flervalg id="tr-bookingvalg" etikett="Kan bookes som" valg={Object.entries(BOOKING_NAVN)} verdi={skjema.booking} onEndre={sett('booking')}
                  disabled={!kanEndre} hjelp="«Tidlig i uken» og «Sent i uken» vises bare i bookingen for tjenester som har dem." />
                {skjema.slug !== 'garantihenvendelse' && (
                  <Avkrysning id="tr-kommentarfelt" etikett="Kommentarfelt"
                    hjelp="Leder og mekaniker bestemmer dette per tjeneste. På: bookingen viser et kommentarfelt når tjenesten velges. Servicepakker, dekkskift og annet arbeid må da fylles ut."
                    verdi={skjema.kommentarfelt ?? kommentarSomStandard(skjema)} onEndre={sett('kommentarfelt')} />
                )}
              </>
            ) : (
              <div className="flex flex-wrap gap-3">
                <Tallfelt id="tr-levering" etikett="Leveringstid" enhet="virkedager" hjelp="Tomt: standard" verdi={skjema.leveringstidDager} onEndre={sett('leveringstidDager')} />
                <Avkrysning id="tr-ukentlig" etikett="Samles ukentlig" hjelp="F.eks. slip: gjøres på fast ukedag (se Kapasitet)." verdi={skjema.samlesUkentlig} onEndre={sett('samlesUkentlig')} />
              </div>
            )}
            {butikker.length > 0 && (
              <Flervalg id="tr-butikker" etikett="Tilbys i" valg={butikker.map((b) => [b.butikkid, b.butikknavn])} verdi={skjema.tillattButikkIds}
                onEndre={sett('tillattButikkIds')} disabled={!kanEndre} hjelp="Ingen valgt: alle butikker." />
            )}
          </Seksjon>

          <Seksjon id="tr-grupper" tittel="Grupper og typer">
            <Flervalg id="tr-gruppevalg" etikett="Vises i gruppene" valg={grupper.map((g) => [g._id, `${g.tjenestetype}${g.active === false ? ' (av)' : ''}`])}
              verdi={skjema.grupper} onEndre={sett('grupper')} disabled={!kanEndre}
              hjelp={sykkel ? 'Uten gruppe vises tjenesten ikke i bookingen.' : 'Ski: «Alpint», «Langrenn» eller begge. Skøyter: ingen.'} />
            <Flervalg id="tr-servicetype" etikett="Servicetype" valg={Object.entries(SERVICETYPE_NAVN)} verdi={skjema.servicetype} onEndre={sett('servicetype')} disabled={!kanEndre} />
            <Flervalg id="tr-sykkeltyper" etikett="Passer for" valg={Object.entries(SYKKELTYPE_NAVN)} verdi={skjema.sykkeltyper} onEndre={sett('sykkeltyper')} disabled={!kanEndre} />
          </Seksjon>

          <Seksjon id="tr-sjekk" tittel="Sjekkliste for mekanikeren">
            <Sjekkliste alle={alleSjekkpunkter} tjenester={data.tjenester} valgte={skjema.sjekkpunkter} onEndre={sett('sjekkpunkter')}
              kanEndre={kanEndre} onNyttPunkt={(p) => setEkstraPunkter((l) => [...l, p])} />
          </Seksjon>

          <details className="rounded-2xl border-2 border-slate-200 bg-white p-3 sm:p-3">
            <summary className="cursor-pointer text-base font-bold text-slate-900">Mer</summary>
            <div className="mt-4 space-y-3">
              <div className="flex flex-wrap gap-3">
                <Tallfelt id="tr-sortering" etikett="Rekkefølge" hjelp="Lavest først i gruppen" verdi={skjema.sortering} onEndre={sett('sortering')} />
                <Tallfelt id="tr-antall" etikett="Antall" verdi={skjema.antall} onEndre={sett('antall')} />
              </div>
              <Tekstfelt id="tr-slug" etikett="Slug (adresse)" value={skjema.slug} onChange={(e) => sett('slug')(e.target.value.toLowerCase())}
                hjelp={viktigSlug
                  ? 'Denne brukes av koden (dekkhotell, sykkelhotell eller statistikk). Ikke endre den.'
                  : ny ? 'Lages av navnet hvis den står tom. Små bokstaver, tall og bindestrek.' : 'Endres sjelden: bookinger og statistikk kjenner tjenesten på denne.'}
                readOnly={viktigSlug} />
              <Tekstfelt id="tr-verkstedmal" etikett="Verkstedmal" flerlinjet value={skjema.verkstedmal} onChange={(e) => sett('verkstedmal')(e.target.value)} />
              <Tekstfelt id="tr-seotittel" etikett="SEO-tittel" value={skjema.seoTittel} onChange={(e) => sett('seoTittel')(e.target.value)} maxLength={200} />
              <Tekstfelt id="tr-seobeskrivelse" etikett="SEO-beskrivelse" flerlinjet value={skjema.seoBeskrivelse} onChange={(e) => sett('seoBeskrivelse')(e.target.value)} maxLength={400} />
            </div>
          </details>
        </fieldset>

        {status.type === 'feil' && <Melding type="feil">{status.tekst}</Melding>}
        {status.type === 'ok' && <Melding type="ok">{status.tekst}</Melding>}
        {kanEndre && (
          <div className="sticky bottom-0 -mx-1 flex flex-wrap gap-2 rounded-xl bg-white/95 p-1 py-1.5 backdrop-blur">
            <button type="submit" disabled={status.type === 'lagrer'} className={knappPrimar}>
              <Ikon navn="hake" className="w-6 h-6" /> {status.type === 'lagrer' ? 'Lagrer …' : ny ? 'Opprett tjeneste' : 'Lagre'}
            </button>
            <button type="button" onClick={() => navigate(TILBAKE)} className={knappSekundar}>Avbryt</button>
          </div>
        )}
      </form>

      {tjeneste && <Bilder tjeneste={tjeneste} kanEndre={kanEndre} onEndret={hent} />}

      {tjeneste && <Endringslogg endringer={tjeneste.endringer} />}
    </div>
  );
}
